import { ReactComponent as EmptyPanelDroneIcon } from '@assets/components/panel/notification/empty-panel-drone-icon.svg';
import { Button, Center, For, HStack, Icon, Separator, Text, VStack } from '@chakra-ui/react';
import { CockpitPanelIntlKeysEnum } from '@components/cockpit/cockpit.types';
import { PANEL_ROUTES } from '@components/cockpit/CockpitRoutes';
import { CockpitEditorLayout } from '@components/cockpit/layouts/CockpitEditorLayout';
import { CheckboxListFilter } from '@components/cockpit/panels/CheckboxListFilter';
import {
  PerimeterCamerasExportButton,
  SensorConfigurationsExportButton,
} from '@components/cockpit/panels/sensorInstallation/SensorExportButtons';
import SensorGroup from '@components/cockpit/panels/sensorInstallation/SensorGroup';
import {
  PerimeterCamerasImportButton,
  SensorConfigurationsImportButton,
} from '@components/cockpit/panels/sensorInstallation/SensorImportButtons';
import { useSensorInstallationContext } from '@components/cockpit/panels/sensorInstallation/SensorInstallationContext';
import { getInstallationStatusItems } from '@components/cockpit/panels/sensorInstallation/sensorOverviewStatus.items';
import { SortEnum } from '@components/cockpit/panels/sensorInstallation/useSensorFilterState';
import ToggleButtonGrid from '@components/common/inputs/buttons/ToggleButtonGrid';
import CustomChipList from '@components/common/inputs/CustomChipList';
import { ScrollArea } from '@components/ui/ScrollArea';
import { useAppSelector } from '@hooks/redux.hooks';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { hasActionRoleOnSensors, hasAnyActionRoleOnSensors, hasRole, hasRoles } from '@redux/authent/authent.selectors';
import { updateSelectedMonitoringSensorUniqueKey } from '@redux/global/global.reducer';
import { selectSensorConfigurationsWithShorthand } from '@redux/sensors/sensors.selectors';
import { RootState } from '@redux/store';
import {
  filterSensorsWithoutStatusFilter,
  getEnabledSensorTypes,
  groupAndEnsureKeys,
  sortAndConvertToMap,
} from '@utils/filter.utils';
import { CategoryEnum } from '@utils/sensors/configuration.constants';
import { sumBy } from 'lodash';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import { RoleEnum } from '@/types/authent/roles.types';
import { ActionEnum } from '@/types/commons/commons.types';
import { LadSensorAbbrKey, PerimeterSensorAbbrKey, SensorInstallationStatusEnum } from '@/types/filters.types';
import { RoutesEnum } from '@/types/routes.types';
import {
  GetSensorOrSubSensorConfigurationWithShorthand,
  SensorTypeWithShorthand,
} from '@/types/sensor/sensorInstallation.types';

import { CreatedSensorModal, DeleteSensorModal } from './SensorInstallationModals';

const CREATE_AND_EDIT_ACTIONS = [ActionEnum.CREATE, ActionEnum.EDIT];
const CREATE_AND_EDIT_PERIMETER_CAMERA_ROLES = [RoleEnum.CREATE_PERIMETER_CAMERA, RoleEnum.EDIT_PERIMETER_CAMERA];

export function SensorOverviewPanel() {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const {
    state,
    canReset,
    createdSensor,
    setCreatedSensor,
    updateCategories,
    updateStatus,
    updateFilterState,
    setSortBy,
    resetFilters,
  } = useSensorInstallationContext();
  const sensorConfigurations = useAppSelector(selectSensorConfigurationsWithShorthand);

  const canExport = useAppSelector((state) => hasRole(state, RoleEnum.EXPORT));
  const hasAnyAccessRoleOnSensors: boolean = useAppSelector((state: RootState) =>
    hasActionRoleOnSensors(state, ActionEnum.ACCESS),
  );
  const hasAccessOnPerimeterCameras = useAppSelector((state) => hasRole(state, RoleEnum.ACCESS_PERIMETER_CAMERA));

  const canImport = useAppSelector((state) => hasRole(state, RoleEnum.IMPORT));
  const hasAnyCreateOrEditRoleOnSensors: boolean = useAppSelector((state: RootState) =>
    hasAnyActionRoleOnSensors(state, CREATE_AND_EDIT_ACTIONS),
  );
  const hasCreateOrEditRoleOnPerimeterCameras = useAppSelector((state) =>
    hasRoles(state, CREATE_AND_EDIT_PERIMETER_CAMERA_ROLES),
  );

  const dispatchSelectedMonitoringSensorUniqueKey = useWithDispatch(updateSelectedMonitoringSensorUniqueKey);

  const chipOptions = Object.values(SortEnum).map((value) => ({
    label: formatMessage({ id: `components.sensorInstallation.filters.sensors.sortEnum.${value}` }),
    value: value,
    isDisabled: value === SortEnum.STATUS,
  }));

  const { filteredSensors, filteredSensorsWithoutStatus } = useMemo(() => {
    const enabledTypes = getEnabledSensorTypes(state);
    const filteredSensorsWithoutStatus = filterSensorsWithoutStatusFilter(sensorConfigurations, enabledTypes);
    const filtered = filteredSensorsWithoutStatus.filter(() =>
      state.status.get(SensorInstallationStatusEnum.OPERATIONAL),
    );
    const grouped = groupAndEnsureKeys(filtered, enabledTypes);

    return { filteredSensors: sortAndConvertToMap(grouped), filteredSensorsWithoutStatus };
  }, [sensorConfigurations, state]);

  const isChecked = (status: SensorInstallationStatusEnum) => state.status.get(status);
  const onChange = (status: SensorInstallationStatusEnum) => updateStatus(status);
  const statusesCheckboxItems = getInstallationStatusItems(isChecked, onChange, filteredSensorsWithoutStatus.length);

  const resultCount = sumBy(Array.from(filteredSensors.values()), (sensors) => sensors.length);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const displayContent = () => {
    return filteredSensors.size === 0 ? (
      <Center height="100%" width="100%" backgroundColor="neutral.black">
        <VStack gap={2} paddingTop="58px">
          <Icon width="194px" height="194px" as={EmptyPanelDroneIcon} />
          <Text color="neutral.200" textAlign="center" userSelect="none">
            <FormattedMessage id="components.sensorInstallation.filters.noResult" />
          </Text>
        </VStack>
      </Center>
    ) : (
      <For each={Array.from(filteredSensors)}>
        {([type, sensors]: [
          SensorTypeWithShorthand,
          GetSensorOrSubSensorConfigurationWithShorthand<SensorTypeWithShorthand>[],
        ]) => <SensorGroup key={`sensor-group-${type}`} type={type} sensors={sensors} setOpen={setOpenDeleteModal} />}
      </For>
    );
  };

  return (
    <HStack width="100%" height="100%" gap={0.25}>
      <VStack
        gap={0.25}
        backgroundColor="neutral.black"
        height="100%"
        width="400px"
        flexShrink={0}
        userSelect="none"
        alignItems="start"
      >
        <HStack width="100%" height="56px" flexShrink={0} backgroundColor="neutral.850" paddingX={2}>
          <Button
            variant="toggle"
            size="toggleS"
            data-active={canReset}
            disabled={!canReset}
            onClick={() => canReset && resetFilters()}
          >
            <Text>
              <FormattedMessage id="components.sensorInstallation.filters.reset" />
            </Text>
          </Button>
        </HStack>
        <ScrollArea>
          <VStack
            height="100%"
            gap={0}
            backgroundColor="neutral.850"
            separator={<Separator size="md" borderColor="neutral.900" />}
          >
            <VStack gap={2} paddingX={2} paddingY={3} alignItems="start" width="100%">
              <Text fontWeight="medium" color="neutral.300">
                <FormattedMessage id="components.sensorInstallation.filters.categories" />
              </Text>
              <HStack width="100%" gap={1}>
                <For each={Array.from(Object.values(CategoryEnum))}>
                  {(category) => (
                    <Button
                      variant="toggle"
                      size="toggleM"
                      flex={1}
                      key={category}
                      data-active={state.categories.has(category)}
                      data-half-active={
                        !state.categories.has(category) &&
                        (category === CategoryEnum.LAD
                          ? [...state.ladSensors.values()].some((value) => value)
                          : [...state.perimeterSensors.values()].some((value) => value))
                      }
                      onClick={() => updateCategories(category)}
                    >
                      <FormattedMessage id={`components.monitoring.filter.${category}`} />
                    </Button>
                  )}
                </For>
              </HStack>
            </VStack>
            <ToggleButtonGrid
              label={formatMessage({ id: 'components.sensorInstallation.filters.types' })}
              buttons={
                new Map<CategoryEnum, Map<LadSensorAbbrKey | PerimeterSensorAbbrKey, boolean>>(
                  Array.from(Object.values(CategoryEnum), (typeValue) => {
                    switch (typeValue) {
                      case CategoryEnum.LAD: {
                        return [typeValue, state.ladSensors];
                      }
                      case CategoryEnum.PERIM: {
                        return [typeValue, state.perimeterSensors];
                      }
                    }
                  }),
                )
              }
              onClick={(type, label) => updateFilterState(type, label)}
            />

            <VStack alignItems="flex-start" paddingX={2} paddingY={3} gap={2} width="100%">
              <Text fontWeight="medium" color="neutral.300">
                <FormattedMessage id="components.sensorInstallation.filters.status" />
              </Text>
              <CheckboxListFilter items={statusesCheckboxItems} />
            </VStack>
          </VStack>
        </ScrollArea>
      </VStack>
      <CockpitEditorLayout
        showActionBar={false}
        dialog={[
          <DeleteSensorModal key="delete-modal" open={openDeleteModal} onCancel={() => setOpenDeleteModal(false)} />,
          <CreatedSensorModal
            key="created-modal"
            open={createdSensor !== null}
            onCancel={() => setCreatedSensor(null)}
            onConfirm={(uniqueKey: string) => {
              setCreatedSensor(null);
              navigate(`${RoutesEnum.COCKPIT}/${PANEL_ROUTES[CockpitPanelIntlKeysEnum.MONITORING]}`);
              dispatchSelectedMonitoringSensorUniqueKey(uniqueKey);
            }}
          />,
        ]}
      >
        <HStack backgroundColor="neutral.850" width="100%" height="56px" paddingLeft={3} justifyContent="space-between">
          <Text fontWeight="medium" color="neutral.200" lineHeight={14}>
            <FormattedMessage id="components.sensorInstallation.filters.result" values={{ count: resultCount }} />
          </Text>
          <HStack gap={0}>
            <HStack gap={1} marginRight={3}>
              <Text color="neutral.200">
                <FormattedMessage id="components.sensorInstallation.filters.sortBy" />
              </Text>
              <CustomChipList
                availableOptions={chipOptions}
                selectedOptions={chipOptions.filter((option) => option.value === state.sortBy)}
                distinctValues={[SortEnum.TYPE, SortEnum.STATUS]}
                onChange={(value) => setSortBy(value[0] as SortEnum)}
                isDisabled
              />
            </HStack>
            {canExport && hasAnyAccessRoleOnSensors && <SensorConfigurationsExportButton />}
            {canImport && hasAnyCreateOrEditRoleOnSensors && <SensorConfigurationsImportButton />}
            {canExport && hasAccessOnPerimeterCameras && <PerimeterCamerasExportButton />}
            {canImport && hasCreateOrEditRoleOnPerimeterCameras && <PerimeterCamerasImportButton />}
          </HStack>
        </HStack>
        <ScrollArea>
          <VStack width="100%" height="100%" backgroundColor="neutral.black" gap={0.25}>
            {displayContent()}
          </VStack>
        </ScrollArea>
      </CockpitEditorLayout>
    </HStack>
  );
}
